import { useEffect, useState, type ReactNode } from 'react';
import './app-shell.css';

export interface AppShellProps {
  sidebar: ReactNode;
  /** Renders above the content on narrow viewports, with the drawer toggle. */
  mobileBar: (props: { onOpenNav: () => void }) => ReactNode;
  /** The "Viewing as / Quit" bar, when a session is impersonated. */
  banner?: ReactNode;
  children: ReactNode;
}

/**
 * The dark sidebar and light work surface.
 *
 * Owns exactly one piece of state — whether the drawer is open — because that is the
 * only thing both the sidebar and the content area need to agree on. Everything else
 * about the layout is CSS.
 *
 * Below --bp-drawer the sidebar becomes a fixed off-canvas drawer with a scrim. The
 * drawer closes on Escape and on route change, which the prototype did neither of: a
 * drawer that stays open over the page it just navigated to is a trap on a phone.
 */
export function AppShell({ sidebar, mobileBar, banner, children }: AppShellProps) {
  const [navOpen, setNavOpen] = useState(false);

  useEffect(() => {
    if (!navOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setNavOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [navOpen]);

  return (
    <div className="appShell">
      <div
        className="appShellScrim"
        data-open={navOpen}
        onClick={() => setNavOpen(false)}
        aria-hidden="true"
      />

      <div className="appShellSidebar" data-open={navOpen}>
        {sidebar}
      </div>

      {banner}

      <main className="appShellMain">
        {mobileBar({ onOpenNav: () => setNavOpen(true) })}
        {children}
      </main>
    </div>
  );
}
